import { Component, ElementRef, forwardRef, OnDestroy, OnInit, Renderer2, Inject, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AuthenticationService, Credentials } from 'app/core/index';
import { finalize } from 'rxjs/operators';
import { RandomUsernameService } from 'app/core/random-username.service';
import { ModalService } from 'app/shared/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { ErrorManagerService } from 'app/core/error-manager.service';
import { AlertService } from '@app/core/alert.service';
import * as amplitude from 'amplitude-js';
import { JackpotService } from '@app/core/jackpot.service';
import { Subscription } from 'rxjs/Subscription';
import { environment } from '@env/environment';
import { RecaptchaComponent } from 'ng-recaptcha';
import { FormSelectOption } from '@app/shared/form/inputs/form-select.component';
import { getNames, alpha2ToAlpha3 } from 'i18n-iso-countries';

@Component({
  selector: 'app-signup-step-1-modal',
  templateUrl: './signup-modal.template.html',
  styleUrls: ['./signup-modal.template.scss'],
  animations: [],
})
export class SignupStep1ModalComponent implements OnInit, OnDestroy {
  form: FormGroup;
  loading = false;
  errors: string = null;
  success: string = null;

  recaptchaEnabled = environment.recaptchaEnabled;

  showJackpot: boolean = false;
  _jackpot: Subscription = null;

  jackpotValue: any = [];

  @ViewChild('captchaRef') captchaRef: RecaptchaComponent;

  countries: FormSelectOption[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private auth: AuthenticationService,
    private activatedRoute: ActivatedRoute,
    private nick: RandomUsernameService,
    private translate: TranslateService,
    private modalService: ModalService,
    private errorManager: ErrorManagerService,
    protected renderer: Renderer2,
    private randomUsername: RandomUsernameService,
    private alertService: AlertService,
    private jackpotService: JackpotService
  ) {
    const countryNames = getNames('en');
    for (const key in countryNames) {
      if (countryNames.hasOwnProperty(key)) {
        this.countries.push({
          option: alpha2ToAlpha3(key),
          value: countryNames[key],
        });
      }
    }
  }

  public ngOnInit() {
    this.createForm();

    this._jackpot = this.jackpotService.jackpotInfo$.subscribe((data: any) => {
      this.showJackpot = !!data;
      if (data && !this.jackpotValue.length) {
        this.buildJackpotValue(this.jackpotService._jackpotInfo.rewardTokenAmount);
      }
    });
  }

  public ngOnDestroy() {
    if (this._jackpot) {
      this._jackpot.unsubscribe();
    }
  }

  requestCaptcha() {
    this.loading = true;
    this.captchaRef.execute();
  }

  submit($event: any) {
    if (this.recaptchaEnabled) {
      this.form.get('g-recaptcha-response').patchValue($event);
    }
    if (this.form.invalid) {
      this.validateAllFormFields(this.form);
      return;
    }
    this.loading = true;
    this.auth
      .createAccount(this.form.value)
      .pipe(
        finalize(() => {
          this.form.markAsPristine();
        })
      )
      .subscribe(
        (result: any) => {
          amplitude.getInstance().logEvent('Onboarding - Create Account', {
            Email: this.form.get('email').value,
            Username: this.form.get('username').value,
          });
          this.router.navigate(['/', { outlets: { modal: 'signup-verify' } }]);
        },
        (error: any) => {
          if (this.recaptchaEnabled) {
            this.captchaRef.reset();
          }
          this.loading = false;
          this.alertService.error({
            title: 'Signup error!',
            text: this.errorManager.errorToPrint(error.json()),
            timer: 5,
          });
        }
      );
  }

  private createForm(): void {
    const username = this.auth.guestUsername || this.randomUsername.random();
    const email = this.auth.guestEmail || '';

    const controlsConfig = {
      username: [username, [Validators.required]],
      email: [email, [Validators.required, Validators.email]],
      password: ['', Validators.required],
      agree: [false, Validators.requiredTrue],
      country: ['', Validators.required],
    };
    if (this.recaptchaEnabled) {
      controlsConfig['g-recaptcha-response'] = [''];
    }
    this.form = this.formBuilder.group(controlsConfig);
  }

  public randomize($event: Event): void {
    $event.preventDefault();
    this.form.get('username').patchValue(this.randomUsername.random());
  }

  validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  openTC() {
    this.modalService.openTermsAndConditions();
  }

  openPP() {
    this.modalService.openPrivacyPolicy();
  }

  buildJackpotValue(num: number) {
    const numString = num.toString();
    for (let i = numString.length; i > 0; i--) {
      if (this.jackpotValue.filter(item => this.isNumber(item)).length % 3 === 0 && this.jackpotValue.length !== 0) {
        this.jackpotValue.push(',');
      }
      this.jackpotValue.push(Number(numString[i - 1]));
    }
    this.jackpotValue.reverse();
  }

  isNumber(val: any) {
    return typeof val === 'number';
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/workspace/Prod_web-client/src/app/_v2/template/modals/signup/1/signup-modal.component.ts